/**
 * Textarea/JSON-Editor Editor
 * Handles configuration for textarea and json-editor controls
 */

import React from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';
import { ExpressionBuilderSection } from './ExpressionBuilderSection';
import { TEXT_EXPRESSION_DEFAULT_FORMAT } from '@atlas-forms/player-components-react';

/**
 * Render function for textarea/json-editor controls.
 *
 * Shared by both editors below, so nothing type-specific may be added here — see
 * `renderTextareaEditor` for where `textarea`'s own additions go.
 */
function renderRowsSection(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Control Configuration</h4>
      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Rows
          <input
            type="number"
            min="1"
            value={(control.config?.rows as number) ?? 4}
            onChange={(e) => onConfigChange('rows', e.target.value ? parseInt(e.target.value) : 4)}
            className="af-properties-tab__input"
            placeholder="4"
          />
        </label>
      </div>
    </div>
  );
}

/**
 * Render function for `textarea` only: the shared rows section plus Plan/05 §3B's opt-in
 * "Enable Expression Builder" toggle.
 *
 * The toggle is **not** in `renderRowsSection` on purpose. `json-editor` reuses that function, but
 * `FormField.tsx`'s `case 'json-editor':` has no §3B branch — only `case 'text':` and
 * `case 'textarea':` do — so a toggle shown on a JSON editor would be a switch that silently does
 * nothing. If `json-editor` ever wants this, it needs the runtime case change first.
 */
function renderTextareaEditor(props: PropertyEditorProps) {
  return (
    <>
      {renderRowsSection(props)}
      {/*
        `TEXT_EXPRESSION_DEFAULT_FORMAT` is the same constant `FormField.tsx`'s `case 'textarea':`
        passes `ExpressionBuilderTrigger`, imported rather than restated, so the "(default)" marker
        cannot disagree with the fallback the runtime applies.
      */}
      <ExpressionBuilderSection
        control={props.control}
        onConfigChange={props.onConfigChange}
        defaultOutputFormat={TEXT_EXPRESSION_DEFAULT_FORMAT}
      />
    </>
  );
}

/**
 * TextareaEditor - Handles textarea control configuration
 */
export const TextareaEditor: PropertyEditor = {
  type: 'textarea',
  label: 'Textarea',
  description: 'Configure rows and Expression Builder support for a textarea control',
  render: renderTextareaEditor,
};

/**
 * JSONEditorEditor - Uses the shared rows section only (see `renderTextareaEditor` for why it does
 * not get the Expression Builder toggle).
 */
export const JSONEditorEditor: PropertyEditor = {
  type: 'json-editor',
  label: 'JSON Editor',
  description: 'Configure rows for JSON editor control',
  render: renderRowsSection,
};
